
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { Icon } from '@/components/ui/icon'

const inlineRenameVariants = cva(
  'relative inline-flex items-center transition-colors',
  {
    variants: {
      variant: {
        // Completely invisible - inherits all parent styling
        invisible: '',
        // Subtle hover indication
        ghost: 'hover:bg-muted/50 rounded-sm',
        // Clear input field styling
        input: '',
        // Outline variant
        outline: '',
        // Click to edit variant with visual feedback
        clickable:
          'cursor-pointer hover:bg-muted/30 rounded-sm transition-all duration-200 hover:shadow-sm',
      },
      size: {
        sm: 'gap-1',
        md: 'gap-1.5',
        lg: 'gap-2',
        xl: 'gap-2.5',
      },
    },
    defaultVariants: {
      variant: 'invisible',
      size: 'md',
    },
  }
)

const inlineRenameInputVariants = cva(
  'bg-transparent border-none outline-none focus:outline-none',
  {
    variants: {
      variant: {
        invisible: 'p-0 m-0',
        ghost: 'p-0 m-0',
        input:
          'px-2 py-1 bg-background border border-input rounded-md focus:ring-1 focus:ring-ring',
        outline:
          'px-2 py-1 border border-muted-foreground/30 rounded-md focus:border-ring focus:ring-1 focus:ring-ring',
        clickable: 'p-0 m-0',
      },
      size: {
        sm: 'text-xs',
        md: 'text-sm',
        lg: 'text-base',
        xl: 'text-lg',
      },
    },
    defaultVariants: {
      variant: 'invisible',
      size: 'md',
    },
  }
)

const inlineRenameTextVariants = cva('', {
  variants: {
    size: {
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-base',
      xl: 'text-lg',
    },
  },
  defaultVariants: {
    size: 'md',
  },
})

export interface InlineRenameProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>,
    VariantProps<typeof inlineRenameVariants> {
  /** Current text value */
  value: string
  /** Callback when value is saved */
  onSave: (newValue: string) => Promise<void> | void
  /** Callback when edit is cancelled */
  onCancel?: () => void
  /** Whether component is in editing mode */
  isEditing: boolean
  /** Callback to start editing (for clickable variant) */
  onStartEdit?: () => void
  /** Enable double-click to edit (works with all variants) */
  enableDoubleClick?: boolean
  /** Icon or element to show before text/input */
  iconBefore?: React.ReactNode
  /** Icon or element to show after text/input */
  iconAfter?: React.ReactNode
  /** Placeholder text for input */
  placeholder?: string
  /** Auto focus input when editing starts */
  autoFocus?: boolean
  /** Show save/loading button */
  showSaveButton?: boolean
  /** Loading state - disables input and shows loading indicator */
  isLoading?: boolean
  /** Custom text styling (overrides size variant) */
  textClassName?: string
  /** Custom input styling (overrides variant styling) */
  inputClassName?: string
  /** Disable save on blur */
  disableSaveOnBlur?: boolean
}

const InlineRename = React.forwardRef<HTMLDivElement, InlineRenameProps>(
  (
    {
      value,
      onSave,
      onCancel,
      isEditing,
      onStartEdit,
      variant,
      size,
      className,
      iconBefore,
      iconAfter,
      placeholder,
      autoFocus = true,
      showSaveButton = false,
      isLoading = false,
      textClassName,
      inputClassName,
      disableSaveOnBlur = false,
      enableDoubleClick = false,
      ...props
    },
    ref
  ) => {
    const [editValue, setEditValue] = useState(value)
    const [isSaving, setIsSaving] = useState(false)
    const [containerWidth, setContainerWidth] = useState<number | undefined>(
      undefined
    )
    const inputRef = useRef<HTMLInputElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)

    // Update edit value when prop value changes
    useEffect(() => {
      setEditValue(value)
    }, [value])

    // Reset container width when not editing
    useEffect(() => {
      if (!isEditing) {
        setContainerWidth(undefined)
      }
    }, [isEditing])

    // Focus input when editing starts
    useEffect(() => {
      if (isEditing && autoFocus && inputRef.current) {
        inputRef.current.focus()
        // Move cursor to end instead of selecting all
        const length = inputRef.current.value.length
        inputRef.current.setSelectionRange(length, length)
      }
    }, [isEditing, autoFocus])

    const handleSave = async () => {
      if (isSaving) return

      const trimmedValue = editValue.trim()
      if (!trimmedValue || trimmedValue === value) {
        handleCancel()
        return
      }

      setIsSaving(true)
      try {
        await onSave(trimmedValue)
        // ✅ Optimistic update: Set local state immediately for instant UI feedback
        // This ensures UI shows the new value even if parent state update is async
        setEditValue(trimmedValue)
        // Success - exit edit mode
        onCancel?.()
      } catch (error) {
        console.error('Failed to save:', error)
        // On error, revert to original value
        setEditValue(value)
      } finally {
        setIsSaving(false)
      }
    }

    const handleCancel = () => {
      setEditValue(value)
      setIsSaving(false)
      onCancel?.()
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleSave()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        handleCancel()
      }
    }

    const handleTextClick = (e: React.MouseEvent) => {
      // Only handle click for clickable variant
      // For other variants (invisible, ghost), let event bubble naturally
      if (variant === 'clickable' && !isEditing && onStartEdit) {
        e.stopPropagation()
        onStartEdit()
      }
    }

    const handleTextDoubleClick = (e: React.MouseEvent) => {
      if (enableDoubleClick && !isEditing && onStartEdit) {
        e.stopPropagation()
        onStartEdit()
      }
    }

    const combinedRef = useCallback(
      (node: HTMLDivElement) => {
        containerRef.current = node
        if (typeof ref === 'function') {
          ref(node)
        } else if (ref) {
          ref.current = node
        }
      },
      [ref]
    )

    const containerProps = {
      ref: combinedRef,
      className: cn(inlineRenameVariants({ variant, size }), className),
      style: {
        ...(containerWidth && { width: `${containerWidth}px` }),
        ...props.style,
      },
      ...props,
    }

    if (!isEditing) {
      return (
        <div {...containerProps}>
          {iconBefore}
          <span
            className={cn(
              inlineRenameTextVariants({ size }),
              textClassName,
              (variant === 'clickable' || enableDoubleClick) && 'cursor-pointer'
            )}
            // Only attach onClick for clickable variant to prevent event bubbling
            {...(variant === 'clickable' && { onClick: handleTextClick })}
            // Only attach onDoubleClick if enableDoubleClick is true
            {...(enableDoubleClick && { onDoubleClick: handleTextDoubleClick })}
          >
            {value}
          </span>
          {iconAfter}
        </div>
      )
    }

    return (
      <div {...containerProps}>
        {iconBefore}
        <input
          type="text"
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={disableSaveOnBlur ? undefined : handleSave}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={cn(
            inlineRenameInputVariants({ variant, size }),
            // Inherit text styling when no explicit input styling
            !inputClassName &&
              !variant?.includes('input') &&
              'font-inherit leading-inherit text-inherit',
            inputClassName
          )}
          autoComplete="off"
          spellCheck="false"
          disabled={isSaving || isLoading}
          style={{
            // Use container width for consistent sizing, fallback to dynamic width
            width: containerWidth
              ? `${containerWidth - (iconBefore ? 24 : 0) - (iconAfter ? 24 : 0) - (showSaveButton ? 32 : 0)}px`
              : undefined,
            ...(variant === 'invisible' &&
              !containerWidth && {
                minWidth: '4ch',
                width: `${Math.max(editValue.length + 1, 6)}ch`,
                maxWidth: '100%',
              }),
          }}
        />
        {iconAfter}
        {showSaveButton && (
          <button
            type="button"
            className={cn(
              'text-muted-foreground hover:bg-muted hover:text-foreground ml-2 flex h-5 w-5 items-center justify-center rounded transition-colors',
              isSaving && 'cursor-not-allowed opacity-50'
            )}
            onClick={!isSaving ? handleSave : undefined}
            disabled={isSaving || isLoading}
          >
            {isSaving ? (
              <Icon name="loader-2" className="h-3 w-3 animate-spin" />
            ) : (
              <Icon name="check" className="h-3 w-3" />
            )}
          </button>
        )}
      </div>
    )
  }
)

InlineRename.displayName = 'InlineRename'

export {
  InlineRename,
  inlineRenameVariants,
  inlineRenameInputVariants,
  inlineRenameTextVariants,
}
